/**
 * ⭐ ЛЕСТНИЦА ПОДАЧИ · окно М1 (27.07) — единственное место, где решается,
 * КАКОЕ упражнение достанется слову.
 *
 * Методика целиком — `knowledge/methodology/delivery-ladder.md`. Коротко, почему так:
 * знание слова растёт ступенями возрастающей трудности (Laufer & Goldstein 2004:
 * пассивное узнавание → активное узнавание → припоминание), и продукция раньше
 * узнавания мешает (Barcroft). До этого окна вид карточки выбирался НАСТРОЙКОЙ и
 * доставался кому попало — отсюда и провал «Собери фразу» на первом же слове.
 *
 * Переключатель между ступенями — бокс Leitner: он уже есть, уже персистится и уже
 * задаёт сторону вопроса (`direction` считает ядро тем же порогом). Своего состояния
 * витрина не заводит — иначе появится второй источник правды о зрелости слова.
 *
 * Ступени методики и чем они показываются СЕГОДНЯ (из готовых видов):
 *   0  Знакомство            слово ни разу не отвечено → «Досье» + сразу проверка
 *   1  Узнаю перевод         бокс 0   → «Выбор» (EN→RU, вопрос ставит ядро)
 *   1а На слух               бокс 1   → «На слух» (звучит слово, текста нет)
 *   2  Достаю слово          бокс 2   → «Выбор» RU→EN (английские варианты собирает ядро) ← М4
 *   3  Слово в предложении   бокс 3   → «Пропуск» (выбрать нужную форму кнопкой) ← М3
 *   4  Вписываю сам          бокс 4   → «Контекст» (ввод формы в пропуск)
 *   5  Фраза на слух         бокс 5   → «Фраза на слух» (звучит предложение целиком) ← М5
 *   6  Беглость              бокс 6+ / статус known → «Беглость» (таймер 5 с)      ← М6
 *
 * ⭐⭐ М3 (28.07): построена ступень 3, ввод руками отодвинут на бокс 4 — «выбрать нужную
 * форму» и «напечатать её» это РАЗНЫЕ ступени.
 * ⭐⭐ М4 (29.07): ступень 2 получила свой бокс обратно, и лестница встала ровно:
 * **номер бокса = номер ступени** (2 → 2, 3 → 3, 4 → 4). Варианты для RU→EN больше не
 * берутся из колоды подхода — их собирает ЯДРО из словаря человека (та же часть речи,
 * тот же уровень, ближайший ранг частотности), поле карточки `distractors_en`.
 * ⭐⭐ М5 (29.07): построена ступень 5 «фраза на слух» — последняя непостроенная в
 * середине. Правило М4 доведено до конца: **бокс 5 → ступень 5, бокс 6+ → ступень 6**,
 * то есть номер бокса = номер ступени на ВСЕЙ лестнице, без исключений.
 * ⭐⭐ М6 (30.07): у верхней ступени появилось СВОЁ тело — вид `fluency` с таймером
 * (было: «Тишина», то есть самооценка без темпа, а беглость — это про темп). «Тишина»
 * при этом жива и не тронута: она осталась ручным отладочным видом.
 * ⛔ «Собери фразу» (`build`) из лестницы выведена решением 28.07 насовсем: полная сборка
 * предложения урезана до ОДНОГО пропуска — это и есть ступень 3. В ручном режиме вид жив.
 *
 * Здесь нет ни DOM, ни сети, ни импортов кроме типов — поэтому правила проверяются
 * запуском: `cd Lang/web && node tools/check-ladder.mjs`.
 */

/**
 * Виды карточки, которые умеет рисовать `screens/session.ts`.
 * ⚠ `listen` (окно М2), `gap` (М3), `hear` (М5) и `fluency` (М6) — виды ТОЛЬКО лестничные:
 * в ручном списке настроек их нет, потому что ручной выбор пишется в `settings.card_mode`,
 * а его белый список живёт в `core/api.py` — трогать ядро ради отладочного тумблера мы не
 * стали. ⭐ Поэтому же ступень 6 не «переименовала» вид `silence`, а завела свой: «Тишина»
 * остаётся выбираемой руками, и ломать принятый вид ради ступени незачем.
 */
export type Mode = "silence" | "dossier" | "choice" | "context" | "build" | "listen" | "gap"
                 | "hear" | "fluency";

/** Как выдаём карточки: по лестнице (дефолт) или человек выбирает вид сам (отладка). */
export type Delivery = "ladder" | "manual";

/**
 * То, что лестница знает о слове. Все поля приходят ОТ ЯДРА — своего состояния витрина
 * не заводит (иначе появится второй источник правды о зрелости слова).
 *
 * ⭐ М3: `function_word` / `ask_construction` — знание о ЯЗЫКЕ, его считает
 * `lexicon.function_word_group` / `lexicon.ask_by_construction`. Витрина не хранит списка
 * служебных слов и не решает, когда переключать: она получает готовый ответ, как с
 * `direction`. Полей может не быть вовсе (старый ответ API) — тогда слово обычное.
 */
export interface Maturity {
  status: string;
  box: number;
  /** «A» — чистое служебное (would), «B» — двойное (have), null/нет — обычное слово. */
  function_word?: "A" | "B" | null;
  /** Готовое решение ядра: спрашивать конструкцией, а не переводом. */
  ask_construction?: boolean;
}

export interface Step {
  /** номер ступени методики для сортировки (не подряд: 3 и 5 ещё не построены; 1а = 1.5) */
  n: number;
  /** как ступень называется в методике и на экране: «0» · «1» · «1а» · «2» … */
  tag: string;
  /** как называется человеку — в подписи подхода */
  title: string;
  /** одной строкой: что сейчас делает человек */
  what: string;
  /** каким из пяти готовых видов ступень показывается сегодня */
  mode: Mode;
  /** ступень 0: сперва знакомство, оценку ставит следующая ступень (одна запись в `reviews`) */
  intro: boolean;
}

/* Пороги. `PRODUCTION_BOX` = `progress.BOX_PRODUCTION_MIN` ядра: тем же числом ядро
   разворачивает вопрос на русский, и второго порога продукции у нас быть не должно. */
export const PRODUCTION_BOX = 2;
/**
 * С какого бокса даём ступень 3 «пропуск выбором».
 * ⭐ М4 (29.07): было 2 → стало **3**. В М3 ступень 3 временно забрала оба бокса, потому что
 * ступень 2 (RU→EN выбором) собиралась из случайных слов подхода и учить ей было нечему.
 * Теперь варианты собирает ядро из своего словаря, и каждая ступень встала на свой бокс:
 * **номер бокса = номер ступени** (2 → 2, 3 → 3, 4 → 4). Материал при этом остаётся
 * посильным: короткий пример держится до бокса 3 включительно (`lexicon.SHORT_MAX_BOX`).
 */
export const GAP_BOX = 3;
/**
 * С какого бокса просим ВПИСАТЬ слово руками (активное припоминание).
 * ⭐ М3: было 3 → стало 4. Между «узнаю в контексте» и «пишу сам» встала ступень 3:
 * выбрать нужную форму и напечатать её — разные ступени (иерархия Laufer & Goldstein),
 * и между ними в наших данных лежит вся разница между «посильно» и «непосильно».
 */
export const WRITE_BOX = 4;
/**
 * ⭐⭐ М5 · С КАКОГО БОКСА ДАЁМ СТУПЕНЬ 5 «ФРАЗА НА СЛУХ» — и почему именно бокс 5.
 *
 * Промт предлагал два кандидата: посадить ступень 5 рядом со ступенью 4 (бокс 4) или
 * завести «новый бокс между 4 и 5». Второго не существует физически — боксы у нас целые
 * и их выдаёт `scheduler` (`INTERVALS` 0·1·3·7·14·30·90, `MAX_BOX = 6`). А первое сломало
 * бы главное, что сложилось в М4: **номер бокса = номер ступени**. Два задания на одном
 * боксе потребовали бы правила «кому из них достанется слово», а вероятностной раздачи мы
 * не заводим — её потом не отладить и человеку не объяснить.
 *
 * Поэтому ступень 5 встаёт на бокс 5, а «Беглость» уезжает на бокс 6 (`MAX_BOX` ядра) —
 * лестница остаётся монотонной и без единого исключения: 0→1 · 1→1а · 2→2 · 3→3 · 4→4 ·
 * **5→5** · 6+→6.
 *
 * ⚠ Тонкость, из-за которой порядок проверок в `stepFor` важен: статус `known` ядро
 * ставит РОВНО с бокса 5 (`scheduler.KNOWN_BOX`), и раньше он уводил слово в «Беглость».
 * Значит ступень 5 надо разобрать ДО статуса — иначе она не наступит никогда. При этом
 * слово, помеченное «знаю» при заливке (бокс 0, статус `known`), как и прежде идёт сразу
 * в «Беглость»: там ступеней не проходили вовсе.
 *
 * ⭐ Вторая ось лестницы (методика, разд. 3.3) сходится сама: короткий пример держится до
 * бокса 3 (`lexicon.SHORT_MAX_BOX`), значит на боксе 5 карточка отдаёт ДЛИННЫЙ пример —
 * ровно тот, который озвучен у всех 2869 слов. Материал под ступень готов без правок ядра.
 */
export const HEAR_BOX = 5;
/**
 * С какого бокса слово считается своим и гоняется на скорость.
 * ⭐ М5: было 5 → стало **6**. Бокс 5 отдан ступени 5; 6 — потолок `scheduler.MAX_BOX`
 * (интервал 90 дней), то есть «беглость» достаётся слову, которое действительно своё.
 */
export const FLUENCY_BOX = 6;

/**
 * ⭐⭐ М6 · СКОЛЬКО ДАЁМ НА ОТВЕТ И ЧТО СЧИТАЕМ «БЕГЛО» — два числа, и оба здесь.
 *
 * Решено 30.07 замером (план М5.1, разд. 2.2): окно **5 с**, зачёт «бегло» — быстрее **3 с**.
 * Беглость — это АВТОМАТИЧЕСКИЙ доступ, а не воспоминание: у своего слова лексическое
 * решение занимает доли секунды, сверху ложатся чтение вопроса и сам тап (≈0.3–0.5 с).
 * Три секунды — «узнал сразу», пять — «вспомнил», дольше — не достал.
 *
 * ⚠ Двигать порог надо ОДНИМ числом (после первой живой серии он наверняка поедет),
 * поэтому ни экран, ни самопроверка своих 3000/5000 не держат — только эти константы.
 */
export const FLUENCY_WINDOW_MS = 5000;
export const FLUENCY_FAST_MS = 3000;

/** Сколько секунд ещё осталось (для видимого таймера): 5 → 4 → … → 0, без минуса. */
export function fluencyLeft(elapsedMs: number): number {
  return Math.max(0, Math.ceil((FLUENCY_WINDOW_MS - elapsedMs) / 1000));
}

/** Время вышло? (тогда карточка закрывается сама и уходит нулём). */
export function fluencyExpired(elapsedMs: number): boolean {
  return elapsedMs > FLUENCY_WINDOW_MS;
}

/**
 * ⭐⭐ М6 · ОЦЕНКА СТУПЕНИ 6 ПО ВРЕМЕНИ. `ms` = сколько человек думал; `null` — он нажал
 * «не помню» или не успел вовсе.
 *
 * ⭐ Ответ уезжает ОБЫЧНЫМ `POST /answer`, как у всех ступеней, и обходных путей ради
 * правила «беглость не двигает Leitner» не надо: на боксе 6 оценки «легко» и «трудно»
 * дают ПОБИТОВО одно состояние — box 6, 90 дней (проверено вызовом `scheduler._next_state`,
 * потолок `MAX_BOX = 6`). Разница остаётся только в журнале `reviews` — по ней потом и
 * считается скорость доступа. Новой таблицы не нужно, схема БД цела.
 *
 * ⚠ ЕДИНСТВЕННОЕ ИСКЛЮЧЕНИЕ, названное вслух: слову, помеченному «знаю» ПРИ ЗАЛИВКЕ
 * (бокс 0 + статус `known`), беглость бокс ПОДНИМАЕТ — и это правильно. Ступеней оно не
 * проходило, интервал у него 0, и без подъёма оно вечно висело бы в очереди «на сегодня».
 * Оценка 2 выводит его из бокса 0, дальше он идёт лестницей как все. Таких слов сегодня 0.
 */
export function fluencyGrade(ms: number | null): 0 | 1 | 2 {
  if (ms === null || fluencyExpired(ms)) return 0;   // не успел / не помню — забыл, и это ноль
  return ms < FLUENCY_FAST_MS ? 2 : 1;               // «узнал сразу» против «вспомнил»
}

/**
 * ⭐⭐ М3 · СЛУЖЕБНОЕ СЛОВО СПРАШИВАЕМ КОНСТРУКЦИЕЙ, А НЕ ПЕРЕВОДОМ.
 *
 * Зеркало `lexicon.CONSTRUCTION_MIN_BOX`: группа B («be · have · do · can · may · need ·
 * let» — у них значение настоящее) до этого бокса спрашивается переводом, дальше —
 * пропуском. Группа A («would · will · should · could · might · must · shall») переводом
 * не спрашивается ВООБЩЕ: «бы» — не перевод, а обломок, а «на слух» на `would` тренирует
 * звук, которого в речи нет (там `I'd`, /wəd/).
 *
 * ⚠ Порог здесь — фолбэк на случай, когда ядро не прислало готовое `ask_construction`
 * (старый ответ API). Правда живёт в ядре: разбор и замеры —
 * `knowledge/methodology/delivery-ladder.md`, раздел 3.5.
 */
export const CONSTRUCTION_BOX = 2;

/** Спрашивать это слово конструкцией? Готовое решение ядра сильнее нашего фолбэка. */
export function byConstruction(c: Maturity): boolean {
  if (typeof c.ask_construction === "boolean") return c.ask_construction;
  const fn = c.function_word ?? null;
  if (fn === "A") return true;
  if (fn === "B") return c.status !== "new" && (c.box || 0) >= CONSTRUCTION_BOX;
  return false;
}

/**
 * ⭐ ОКНО М2 · ПРАВИЛО РАЗДАЧИ СТУПЕНИ 1а — ОДНОЙ СТРОКОЙ: **бокс ровно 1 → на слух**
 * (бокс 0 остаётся ступенью 1 «глазами», бокс 2+ идёт выше по лестнице).
 *
 * Почему именно так — три причины, и ни одна не требует нового состояния в витрине:
 *
 * 1. **Не на первой встрече.** Бокс 1 недостижим, пока на слово не ответили: новое
 *    слово лежит в боксе 0 со статусом `new` и получает ступень 0 + сразу ступень 1
 *    (закон М1). Значит «звучит слово, которого ты никогда не видел» невозможно
 *    по построению, а не по отдельной проверке.
 * 2. **Слово и видно, и слышно.** Бокс двигает ядро (`scheduler`: «легко» → +1,
 *    «трудно» → на месте, «не помню» → 0), поэтому слово само ходит между ступенями:
 *    узнал глазами (бокс 0) → бокс 1 → спрашиваем ухом → узнал → бокс 2, там оно снова
 *    на экране написанным (выбор RU→EN). Споткнулся на слух — бокс падает в 0, и
 *    следующий показ снова глазами. Ступень 1 не вытеснена: она обслуживает бокс 0,
 *    то есть каждое новое и каждое сорвавшееся слово.
 * 3. **Второго источника правды нет.** Всё считается из `box`, который уже персистится
 *    и переживает рестарт. Любая «своя память витрины» (сколько раз слово слышали)
 *    разъехалась бы с ядром на первом же обновлении страницы.
 */
export const LISTEN_BOX = 1;

export const STEPS: Record<number, Step> = {
  0: { n: 0, tag: "0", title: "Знакомство", what: "смотрим и слушаем слово", mode: "dossier", intro: true },
  1: { n: 1, tag: "1", title: "Узнаю перевод", what: "выбери перевод из четырёх", mode: "choice", intro: false },
  // 1а стоит между 1 и 2 — числовой ключ 1.5 нужен только для порядка, человеку виден `tag`.
  1.5: { n: 1.5, tag: "1а", title: "На слух", what: "послушай и выбери перевод", mode: "listen", intro: false },
  2: { n: 2, tag: "2", title: "Достаю слово", what: "выбери английское по переводу", mode: "choice", intro: false },
  3: { n: 3, tag: "3", title: "Слово в предложении", what: "выбери, что стоит в пропуске", mode: "gap", intro: false },
  4: { n: 4, tag: "4", title: "Вписываю сам", what: "впиши слово в предложение", mode: "context", intro: false },
  5: { n: 5, tag: "5", title: "Фраза на слух", what: "послушай фразу и вставь слово", mode: "hear", intro: false },
  6: { n: 6, tag: "6", title: "Беглость", what: "своё слово — успей за 5 секунд", mode: "fluency", intro: false },
};

/**
 * Ступень по зрелости слова. Правило детерминированное и объяснимое одной фразой:
 * «слово окрепло — спрашиваем строже». ⛔ Никакой вероятностной раздачи видов:
 * её потом не отладить и человеку не объяснить.
 *
 * `sound` — можно ли ЗАДАТЬ вопрос звуком прямо сейчас (звук включён настройкой и файл
 * слова есть). Не можем — ступень 1а молча уступает место ступени 1: спуск на ступень
 * ниже это закон М1, и он тут работает буквально («нет данных под ступень — берём
 * ступень ниже, молча»). Решает экран занятия, а не этот файл: настройки звука и
 * состояние плеера живут там.
 *
 * ⭐ М5: `hear` — то же самое для ступени 5: собралась ли она из готовых данных (звук
 * ФРАЗЫ включён и доехал · в примере ровно один пропуск · есть перевод · набралось три
 * кнопки · предложение не длиннее 12 слов). Не собралась — молча ступень 4.
 */
export function stepFor(c: Maturity, sound = true, gap = true, hear = true): Step {
  if (c.status === "new") return STEPS[0];          // ни одного ответа — это первая встреча
  const box = c.box || 0;
  // ⭐ М5: бокс 6 и выше — «Беглость». Слово, помеченное «знаю» при заливке, тоже: оно
  // ступеней не проходило вовсе. ⚠ Но статус `known` приходит уже с бокса 5, поэтому
  // сам бокс 5 разбираем НИЖЕ, до статуса — иначе ступень 5 не наступит никогда.
  // ⚠ М6: именно такому слову (бокс 0 + `known`) беглость бокс ПОДНИМАЕТ — иначе оно
  // вечно висит в очереди с интервалом 0; разбор исключения — у `fluencyGrade`.
  if (box >= FLUENCY_BOX || (c.status === "known" && box < HEAR_BOX)) return STEPS[6];

  // ⭐ М5: ступень 5 стоит ВЫШЕ ввода руками, поэтому и разбирается раньше — и она же
  // единственное задание на слух, которое годится служебному слову (см. HEAR_BOX).
  // Звука фразы нет — ступень ниже, то есть 4, а не «беглость»: спуск идёт ВНИЗ.
  if (box >= HEAR_BOX) return hear ? STEPS[5] : STEPS[4];
  // ⚠ Порядок с `byConstruction` — как в М3: с бокса 4 служебное слово тоже ПЕЧАТАЮТ
  // («I \_\_\_\_\_ love to visit Japan» → впиши `would`), и М5 этого не менял.
  if (box >= WRITE_BOX) return STEPS[4];

  // ⭐⭐ М3: служебное слово спрашиваем ПРОПУСКОМ, а не переводом — на любом боксе, где
  // ядро сказало «конструкцией». Данных под пропуск нет (заготовки/перевода примера) —
  // тогда карточка остаётся ЗНАКОМСТВОМ: показать слово в его обороте честно, а спросить
  // «would → бы» нельзя вообще. Это тот же спуск на ступень ниже, только вниз до нуля.
  if (byConstruction(c)) return gap ? STEPS[3] : STEPS[0];
  if (box >= GAP_BOX) return gap ? STEPS[3] : STEPS[2];   // нет пропуска → ступень ниже
  if (box >= PRODUCTION_BOX) return STEPS[2];             // ⭐ М4: бокс 2 → достаю слово RU→EN
  if (box === LISTEN_BOX && sound) return STEPS[1.5];
  return STEPS[1];
}

/** Значение настройки `delivery` → лестница или ручной выбор. Незнакомое = лестница. */
export function isLadder(value: unknown): boolean {
  return String(value ?? "ladder") !== "manual";
}

/* Виды, которые человек может выбрать РУКАМИ (отладочный режим). ⛔ Без `listen`, `gap`
   и `hear`: ручной выбор уезжает в `settings.card_mode`, а его белый список — в ядре.
   Все три лестничные — они выдаются по зрелости слова, а не пунктом меню. */
const MODES: Mode[] = ["silence", "dossier", "choice", "context", "build"];

/**
 * Вид карточки ДО проверки данных: лестница по зрелости либо ручной выбор человека.
 * Собирается ли этот вид из готовых полей — решает уже экран занятия (`shownMode`):
 * пустых полей человек видеть не должен, поэтому там есть спуск на ступень ниже.
 */
export function modeFor(c: Maturity, delivery: unknown, chosen: unknown, sound = true,
                        gap = true, hear = true): Mode {
  if (!isLadder(delivery)) {
    const raw = String(chosen ?? "");
    return (MODES as string[]).includes(raw) ? (raw as Mode) : "dossier";
  }
  return stepFor(c, sound, gap, hear).mode;
}

/**
 * ⭐ Связка «знакомство → сразу проверка» (закон конкурентов и Pimsleur: первый интервал —
 * секунды, а не следующий день). Новое слово мы сперва ПОКАЗЫВАЕМ, а потом в той же
 * карточке спрашиваем ступенью 1. Оценка при этом ОДНА: знакомство её не ставит.
 * Не собралось узнавание из данных (нет четырёх вариантов) — знакомство остаётся
 * самооценкой, как было.
 */
export function afterIntro(c?: Maturity, gap = true): Mode {
  // ⭐ М3: у служебного слова знакомство ведёт НЕ к выбору перевода (его у `would` нет),
  // а сразу к пропуску — той самой конструкции, ради которой слово и учат.
  if (c && byConstruction(c)) return gap ? STEPS[3].mode : STEPS[0].mode;
  return STEPS[1].mode;
}
